import { useMemo } from "react"

import { Image as AntdImage, Form, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"

import { RoutesPaths } from "../../../../../../../constants/user-routes"
import QuestionField from "../../../../ConfigurePage/components/QuestionField/QuestionField"
import { EVIDENCE_IMAGE_GROUPS, EVIDENCE_IMAGE_TYPES } from "../../../schema/evidenceImages"

import uploadStyles from "../../../../../uploadSlot.module.less"
import groupStyles from "../../group.module.less"
import paneStyles from "../enrichmentPane.module.less"
import styles from "./enrichmentImages.module.less"

const { Text } = Typography

// Per-dispute images: what Chargeback prepared for the packet (source 'prepared') plus
// the merchant's own uploads (source 'merchant'). Local-only, no endpoint yet, so an
// upload becomes a FileReader data URL — the exact fallback the Configure upload saga
// uses when the backend is unavailable.
const EnrichmentImages = ({ images, onChange, isActionable }) => {
  const { t } = useTranslation()

  const imageForType = (typeKey) => images.find((i) => i.type === typeKey)
  const filledCount = images.filter((i) =>
    EVIDENCE_IMAGE_TYPES.some((tp) => tp.key === i.type)
  ).length

  // The QuestionField upload slots read a { typeKey: { url, file_name } } map, the same
  // shape a Configure section holds, so the SAME component renders both.
  const fieldValues = useMemo(() => {
    const out = {}
    images.forEach((img) => {
      if (img.type) out[img.type] = { url: img.url, file_name: img.file_name, s3_key: img.id }
    })
    return out
  }, [images])

  // One image per type: uploading to a filled slot replaces it (same Replace semantics
  // as a Configure locker slot).
  const handleUpload = (typeKey, file) => {
    const reader = new FileReader()
    reader.onload = () =>
      onChange([
        ...images.filter((i) => i.type !== typeKey),
        {
          id: `up-${Date.now()}-${typeKey}`,
          type: typeKey,
          file_name: file.name,
          url: reader.result,
          source: "merchant"
        }
      ])
    reader.readAsDataURL(file)
    return false // keep antd Upload from POSTing anywhere
  }

  // QuestionField's only direct write on an upload slot is Remove (it nulls the key
  // after its own red confirm); uploads and replaces come through onUpload.
  const handleFieldChange = (next) => {
    const removed = Object.keys(fieldValues).filter((k) => !next?.[k])
    if (removed.length) onChange(images.filter((img) => !removed.includes(img.type)))
  }

  return (
    <>
      <Text type="secondary" className={paneStyles.intro}>
        {t(
          "Upload images related specifically to this dispute. Pick the type first so each image lands in the right place in the evidence PDF."
        )}{" "}
        {t("Company-level documents, like your policies and terms, live in")}{" "}
        <a href={RoutesPaths.recover.configure}>{t("Configure")}</a>.
      </Text>
      {/* The exact Evidence-locker experience from Configure Step 6: titled groups of
          labeled upload fields, each rendered by the SAME QuestionField upload slot
          (dragger, preview card, Replace / Remove with its red confirm). Only the data
          source differs: values live on this dispute, not in buildingBlocks. */}
      {isActionable ? (
        <Form layout="vertical" requiredMark={false} className="recover-section-form">
          {EVIDENCE_IMAGE_GROUPS.map((group) => (
            <div
              className={cls(groupStyles.groupCard, groupStyles.groupCardStacked)}
              key={group.key}
            >
              <div className={groupStyles.groupLabel}>{t(group.label)}</div>
              {group.types.map((typeKey) => {
                const tp = EVIDENCE_IMAGE_TYPES.find((x) => x.key === typeKey)
                const img = imageForType(typeKey)
                return (
                  <QuestionField
                    key={typeKey}
                    question={{
                      key: typeKey,
                      label: tp.label,
                      type: "upload",
                      accept: "image/png,image/jpeg",
                      acceptHint: "PNG or JPG. Max 5 MB.",
                      // The prepared note rides the slot's own help line.
                      help:
                        img?.source === "prepared"
                          ? "Prepared by Chargeback from your Configure answers. You can replace it with your own version at any time."
                          : undefined
                    }}
                    sectionState={fieldValues}
                    onChange={handleFieldChange}
                    onUpload={handleUpload}
                  />
                )
              })}
            </div>
          ))}
        </Form>
      ) : filledCount ? (
        // Read-only (closed dispute): just the images that made it in.
        <div className={styles.readonlyList}>
          {EVIDENCE_IMAGE_TYPES.map((tp) => {
            const img = imageForType(tp.key)
            if (!img) return null
            // Same two states QuestionField renders: a data-URL image gets the
            // thumbnail, anything else the filename chip (Configure's seeded-upload look).
            const isImg = /^data:image/i.test(img.url || "")
            return (
              <div className={cls(uploadStyles.slot, uploadStyles.slotDone)} key={tp.key}>
                <Text strong>{t(tp.label)}</Text>
                {isImg ? (
                  <AntdImage
                    src={img.url}
                    alt={t(tp.label)}
                    rootClassName={uploadStyles.thumb}
                    preview={{
                      src: img.url,
                      mask: t("Click to expand"),
                      getContainer: () => document.getElementById("main-layout")
                    }}
                  />
                ) : (
                  <span className={uploadStyles.fileLink}>{img.file_name}</span>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <Text type="secondary">{t("None recorded.")}</Text>
      )}
    </>
  )
}

export default EnrichmentImages
